import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MainLayout from '../layout/MainLayout/MainLayout'
import {
  RailButton,
  IconPlus,
  IconAddWindow,
  IconSearch,
  IconBell,
} from '../layout/NavRail/NavRail'
import NavLocation from '../layout/NavLocation/NavLocation'
import NavList from '../layout/NavList/NavList'
import { NavFilterLead, useFilterOpen } from '../layout/NavList/NavFilterLead'
import SearchroomList from '../components/searchroom/SearchroomList'
import SearchroomWindowBody from '../components/searchroom/SearchroomWindowBody'
import SearchroomFilterPanel from '../components/searchroom/SearchroomFilterPanel'
import { searchroomLabel } from '../components/searchroom/searchroomLabel'
import { WindowGrid, GridDock, useWindowGrid } from '../components/grid'
import {
  buildSearchroomFacets,
  facetsToParams,
  hasActiveFilters,
} from '../components/searchroom/searchroomFacets'
import { useSearchroomFilterOptions } from '../hooks/useSearchroomFilterOptions'
import { useTopics } from '../hooks/useTopics'
import { useSearchroomCache } from '../hooks/useSearchroomCache'
import { useModal } from '../context/ModalContext'
import { ItemBookmarkProvider } from '../components/references/ItemBookmarkContext'
import { deleteSearchroom } from '../services/searchrooms'
import { usePageTitle } from '../components/DocumentTitle'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconEdit = () => (
  <svg {...svgProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
)

const IconDelete = () => (
  <svg {...svgProps}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
)

// Search Room workspace: a collapsible sidebar beside a tiled window grid. Each
// window is a whole searchroom (chat + PDF), so several rooms can be open at once
// for comparison. The grid engine is generic + persisted per tab; this page just
// supplies how a searchroom window renders and its header actions.
function SearchRoomPage() {
  const { openModal } = useModal()
  const [reloadSignal, setReloadSignal] = useState(0)
  const [notice, setNotice] = useState('')
  const searchOpenRef = useRef(false)

  const grid = useWindowGrid({
    storageKey: 'ct-grid-search-v1',
    limits: { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 },
    initialType: 'searchroom',
  })

  // Full room docs for every window that has one (fetched on reload, seeded on
  // selection). Empty windows (entityId null) contribute nothing to fetch.
  const entityIds = useMemo(
    () => grid.columns.flatMap((col) => col.windows.map((w) => w.entityId)).filter(Boolean),
    [grid.columns]
  )
  const { roomsById, seed, remove } = useSearchroomCache(entityIds)

  // Sidebar highlight tracks the active window's room.
  const activeRoomId = useMemo(() => {
    for (const col of grid.columns) {
      const win = col.windows.find((w) => w.id === grid.activeId)
      if (win) return win.entityId
    }
    return null
  }, [grid.columns, grid.activeId])

  // Sidebar filter/sort state (session-only — cleared via the popover).
  const options = useSearchroomFilterOptions()
  const { topics: topicOptions } = useTopics()
  const facets = useMemo(
    () => buildSearchroomFacets({ ...options, topicOptions }),
    [options, topicOptions]
  )
  const [filterValues, setFilterValues] = useState({})
  const [sort, setSort] = useState('updated')
  const [filterOpen, toggleFilter] = useFilterOpen('ct-searchroom-filter-open')

  const filtered = hasActiveFilters(filterValues)
  const filterParams = useMemo(() => facetsToParams(filterValues), [filterValues])

  // Auto-dismiss the "too many windows" notice.
  useEffect(() => {
    if (!notice) return undefined
    const timer = setTimeout(() => setNotice(''), 4000)
    return () => clearTimeout(timer)
  }, [notice])

  // Load a searchroom into the ACTIVE window (the last-focused one). We already
  // hold the room, so seed the cache to skip a fetch. Never creates a window.
  const openRoom = useCallback(
    (room) => {
      if (!room?._id) return
      seed(room)
      grid.selectEntity({ type: 'searchroom', entityId: room._id })
    },
    [seed, grid]
  )
  const openRoomRef = useRef(openRoom)
  openRoomRef.current = openRoom

  // Add a new (empty) window to the grid, honoring the soft cap.
  const handleAddWindow = useCallback(() => {
    if (grid.isFull) {
      setNotice(
        `You can open up to ${grid.limits.maxWindows} windows at once. Close one to add another.`
      )
      return
    }
    grid.addWindow({ type: 'searchroom' })
  }, [grid])

  // Any window showing a deleted room reverts to the empty state (kept, not
  // closed) - mirrors the old project's clear-cell-by-id behavior.
  const clearWindowsForRoom = useCallback(
    (roomId) => {
      grid.columns.forEach((col) =>
        col.windows.forEach((w) => {
          if (w.entityId === roomId) grid.setWindowEntity(w.id, null)
        })
      )
    },
    [grid]
  )

  const handleNewSearchroom = () => {
    openModal('create-searchroom', {
      onCreated: (created) => {
        setReloadSignal((n) => n + 1)
        if (created) openRoomRef.current(created)
      },
    })
  }

  const handleEditRoom = useCallback(
    (room) => {
      if (!room) return
      openModal('edit-searchroom', {
        searchroom: room,
        onSaved: (updated) => {
          seed(updated)
          setReloadSignal((n) => n + 1)
        },
      })
    },
    [openModal, seed]
  )

  const handleDeleteRoom = useCallback(
    (room) => {
      if (!room) return
      openModal('confirm', {
        title: 'Delete searchroom',
        message: `Delete "${room.label}"? This can't be undone.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => deleteSearchroom(room._id),
        onConfirmed: () => {
          clearWindowsForRoom(room._id)
          remove(room._id)
          setReloadSignal((n) => n + 1)
        },
      })
    },
    [openModal, clearWindowsForRoom, remove]
  )

  const handleNotifications = () => {
    // TODO: open notifications
  }

  const changeFacet = useCallback((key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const clearFilters = useCallback(() => setFilterValues({}), [])

  // Open the searchroom finder (rail icon or ⌘/Ctrl+K). Guarded so the shortcut
  // can't stack multiple palettes; the guard resets when the palette closes.
  const handleSearch = useCallback(() => {
    if (searchOpenRef.current) return
    searchOpenRef.current = true
    openModal('searchroom-search', {
      onSelect: (room) => openRoomRef.current(room),
      onClose: () => {
        searchOpenRef.current = false
      },
    })
  }, [openModal])

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        handleSearch()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleSearch])

  const getTitle = useCallback(
    (win) => {
      if (!win.entityId) return 'New window'
      const room = roomsById[win.entityId]
      return room ? searchroomLabel(room) : 'Loading…'
    },
    [roomsById]
  )
  usePageTitle(activeRoomId ? getTitle({ entityId: activeRoomId }) : '')

  const renderWindow = useCallback(
    (win) => {
      if (!win.entityId) {
        return <div className="display-empty">Select a searchroom from the list.</div>
      }
      return (
        <SearchroomWindowBody
          room={roomsById[win.entityId]}
          panes={win.panes}
          onPanesChange={(layout) => grid.setPanes(win.id, layout)}
        />
      )
    },
    [roomsById, grid]
  )

  // Edit/delete only make sense once a room is loaded into the window. They live
  // in the window's "more" menu as labelled rows.
  const renderMenuItems = useCallback(
    (win) => {
      const room = win.entityId ? roomsById[win.entityId] : null
      if (!room) return null
      return (
        <>
          <button type="button" role="menuitem" onClick={() => handleEditRoom(room)}>
            <IconEdit />
            Edit searchroom
          </button>
          <button
            type="button"
            role="menuitem"
            className="is-danger"
            onClick={() => handleDeleteRoom(room)}
          >
            <IconDelete />
            Delete searchroom
          </button>
        </>
      )
    },
    [roomsById, handleEditRoom, handleDeleteRoom]
  )

  return (
    <ItemBookmarkProvider>
    <MainLayout
      storageId="searchroom-layout"
        navLocation={<NavLocation title="Searchrooms" />}
        navActions={[
          {
            key: 'add-window',
            label: 'Add window',
            icon: <IconAddWindow />,
            onClick: handleAddWindow,
          },
          {
            key: 'new',
            label: 'New searchroom',
            icon: <IconPlus />,
            onClick: handleNewSearchroom,
          },
          {
            key: 'search',
            label: 'Search searchrooms',
            icon: <IconSearch />,
            onClick: handleSearch,
          },
        ]}
        navFooter={
          <RailButton label="Notifications" onClick={handleNotifications}>
            <IconBell />
          </RailButton>
        }
        navDock={
          <GridDock
            items={grid.minimized}
            getTitle={getTitle}
            onRestore={grid.restoreWindow}
            onClose={grid.closeWindow}
          />
        }
        nav={
          <NavList>
            <NavFilterLead
              open={filterOpen}
              active={filtered}
              onToggle={toggleFilter}
              onClear={clearFilters}
              clearDisabled={!filtered}
              last={!filterOpen}
            />
            {filterOpen ? (
              <SearchroomFilterPanel
                facets={facets}
                values={filterValues}
                onChange={changeFacet}
                sort={sort}
                onSortChange={setSort}
              />
            ) : null}
            <SearchroomList
              reloadSignal={reloadSignal}
              selectedId={activeRoomId}
              onSelect={openRoom}
              filters={filterParams}
              sort={sort}
              filtered={filtered}
            />
          </NavList>
        }
        display={
          <div className="sr-grid-wrap">
            {notice && <div className="sr-grid-notice">{notice}</div>}
            <div className="sr-grid-body">
              <WindowGrid
                grid={grid}
                renderWindow={renderWindow}
                getTitle={getTitle}
                renderMenuItems={renderMenuItems}
                empty="Select a searchroom to get started."
              />
            </div>
          </div>
        }
      />
    </ItemBookmarkProvider>
  )
}

export default SearchRoomPage
